import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Input,
  RadioGroup,
  Radio,
} from '@nextui-org/react';
import React, { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { IoMdAddCircleOutline } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { decryptData } from '../../utils/encryptData';
import { currencyItem, riskLevelItem } from '../../utils/dataFile';
import DropdownWithSearch from '../../components/Dropdown/DropdownWithSearch.js';
import { submitSignal } from '../../services/signalQuery';
import { validateForm } from '../../utils/Validation/SignalConstraint';
import { message } from 'antd';

function MarketSignalModal() {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();

  const [errors, setErrors] = useState({});

  const [currencyPair, setCurrencyPair] = useState('');
  const [riskLevel, setRiskLevel] = useState('');
  const [orderType, setOrderType] = useState('');
  const [entryValue, setEntryValue] = useState('');
  const [targetLevel1, setTargetLevel1] = useState('');
  const [targetLevel2, setTargetLevel2] = useState('');
  const [targetLevel3, setTargetLevel3] = useState('');
  const [stopLoss, setStopLoss] = useState('');

  const { rl } = useSelector((state) => state.auth);
  const decryptRole = decryptData(rl);

  const queryClient = useQueryClient();

  const closeModal = () => {
    onClose();
    setCurrencyPair('');
    setRiskLevel('');
    setOrderType('');
    setEntryValue('');
    setTargetLevel1('');
    setTargetLevel2('');
    setTargetLevel3('');
    setStopLoss('');
    setErrors({});
  };

  const { mutate: addSignalMutation, isLoading: isAddingSignal } = useMutation({
    mutationFn: submitSignal,
    onError: (error) => {
      console.error('Error adding signal:', error);
      message.error(error?.response?.data?.error || error.message);
    },
    onSuccess: async () => {
      message.success('Signal added successfully');
      await queryClient.invalidateQueries(['signal']);
    },
  });

  const submitForm = async () => {
    const formData = {
      currentPair: currencyPair,
      riskLevel,
      orderType,
      value: entryValue,
      targetLevel1,
      targetLevel2,
      targetLevel3,
      stopLoss,
    };

    const errors = await validateForm(formData);

    if (errors && Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    addSignalMutation(formData);
    onClose();
  };

  const selectCurrencyPair = (value) => {
    setCurrencyPair(value);
  };

  const selectRiskLevel = (value) => {
    setRiskLevel(value);
  };

  return (
    <>
      {decryptRole === 'admin' && (
        <Button
          onPress={onOpen}
          endContent={
            <IoMdAddCircleOutline className="text-btn-color" size={22} />
          }
          className="border-btn-color text-mobile-font md:text-sm text-font-white-color"
          variant="bordered"
          size="sm"
        >
          Add Signal
        </Button>
      )}

      <Modal
        size="2xl"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="center"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Add a Signal
              </ModalHeader>
              <ModalBody>
                {/* Dropdown */}
                <div className="grid grid-cols-2 gap-5 mb-3">
                  <div>
                    <DropdownWithSearch
                      SelectItem={selectCurrencyPair}
                      data={currencyItem}
                      placeholder="Select Pair"
                      isInvalid={Boolean(errors?.currentPair)}
                      error={errors?.currentPair || ''}
                    />
                  </div>

                  <div>
                    <DropdownWithSearch
                      SelectItem={selectRiskLevel}
                      data={riskLevelItem}
                      placeholder="Select confidence level"
                      isInvalid={Boolean(errors?.riskLevel)}
                      error={errors?.riskLevel || ''}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-5 mb-3">
                  {/* Radio btn */}
                  <RadioGroup
                    label="Select order type"
                    orientation="horizontal"
                    className="mb-3 text-gray-500"
                    value={orderType}
                    onValueChange={setOrderType}
                    defaultValue="sellLimit"
                    isInvalid={Boolean(errors?.orderType)}
                  >
                    <Radio value="sellLimit">Sell limit</Radio>
                    <Radio value="buyLimit">Buy limit</Radio>
                  </RadioGroup>

                  <div>
                    <Input
                      label="Entry value"
                      labelPlacement="outside"
                      onChange={(e) => setEntryValue(e.target.value)}
                      placeholder="Enter value"
                      isInvalid={Boolean(errors?.value)}
                      errorMessage={errors?.value || ''}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <Input
                      label="Target level 1"
                      labelPlacement="outside"
                      onChange={(e) => setTargetLevel1(e.target.value)}
                      placeholder="Enter value"
                      isInvalid={Boolean(errors?.targetLevel1)}
                      errorMessage={errors?.targetLevel1 || ''}
                    />
                  </div>
                  <div>
                    <Input
                      label="Target level 2"
                      labelPlacement="outside"
                      onChange={(e) => setTargetLevel2(e.target.value)}
                      placeholder="Enter value"
                      isInvalid={Boolean(errors?.targetLevel2)}
                      errorMessage={errors?.targetLevel2 || ''}
                    />
                  </div>
                  <div>
                    <Input
                      label="Target level 3"
                      labelPlacement="outside"
                      onChange={(e) => setTargetLevel3(e.target.value)}
                      placeholder="Enter value"
                      isInvalid={Boolean(errors?.targetLevel3)}
                      errorMessage={errors?.targetLevel3 || ''}
                    />
                  </div>
                  <div>
                    <Input
                      label="Stop loss"
                      labelPlacement="outside"
                      onChange={(e) => setStopLoss(e.target.value)}
                      placeholder="Enter value"
                      className="text-red-500"
                      isInvalid={Boolean(errors?.stopLoss)}
                      errorMessage={errors?.stopLoss || ''}
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="danger"
                  variant="flat"
                  onPress={closeModal}
                  size="sm"
                  className="text-mobile-font md:text-sm"
                >
                  Cancel
                </Button>
                <Button
                  // className="bg-btn-color  text-mobile-font md:text-sm'"
                  onPress={submitForm}
                  isLoading={isAddingSignal}
                  size="sm"
                >
                  Submit
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

export default MarketSignalModal;
